import { UserManager, WebStorageStateStore, type UserManagerSettings, Log } from 'oidc-client-ts';

declare global {
  interface Window {
    PULSE_OIDC?: {
      enabled: boolean;
      login: () => Promise<void>;
      logout: () => Promise<void>;
      handleCallback: () => Promise<void>;
      getIdToken: () => Promise<string | null>;
    };
    PULSE_OIDC_ENABLED?: boolean;
    PULSE_OIDC_AUTHORITY?: string;
    PULSE_OIDC_CLIENT_ID?: string;
    PULSE_OIDC_SCOPE?: string;
    PULSE_OIDC_REDIRECT_PATH?: string;
  }
}

const enabled = Boolean(window.PULSE_OIDC_ENABLED);

const authority = String(window.PULSE_OIDC_AUTHORITY || '');
const clientId = String(window.PULSE_OIDC_CLIENT_ID || '');
const redirectPath = String(window.PULSE_OIDC_REDIRECT_PATH || '/oidc-callback');
const scope = String(window.PULSE_OIDC_SCOPE || 'openid profile email');

const redirectUri = `${window.location.origin}${redirectPath}`;

const settings: UserManagerSettings = {
  authority,
  client_id: clientId,
  redirect_uri: redirectUri,
  post_logout_redirect_uri: window.location.origin,
  response_type: 'code',
  scope,
  // persist OIDC session only for current browser session
  userStore: new WebStorageStateStore({ store: window.sessionStorage })
};

// Keep logs quiet by default
Log.setLogger(console);
Log.setLevel(Log.NONE);

const manager = new UserManager(settings);

const requireConfigured = () => {
  if (!enabled) {
    return false;
  }
  if (!clientId.trim()) {
    throw new Error('OIDC is enabled but PULSE_OIDC_CLIENT_ID is missing');
  }
  return true;
};

const login = async () => {
  if (!requireConfigured()) return;
  await manager.signinRedirect();
};

const logout = async () => {
  if (!requireConfigured()) return;
  await manager.signoutRedirect();
};

const handleCallback = async () => {
  if (!requireConfigured()) return;
  await manager.signinRedirectCallback();
};

const getIdToken = async (): Promise<string | null> => {
  if (!requireConfigured()) return null;
  const user = await manager.getUser();
  if (!user || user.expired) {
    return null;
  }
  return user.id_token || null;
};

window.PULSE_OIDC = {
  enabled,
  login,
  logout,
  handleCallback,
  getIdToken
};
